import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

function TradingChart({ pair, marketData, socket }) {
  const [chartData, setChartData] = useState([]);
  const [timeframe, setTimeframe] = useState('1h');
  const [chartType, setChartType] = useState('line');
  const [isLoading, setIsLoading] = useState(true);
  const chartRef = useRef(null);

  // Extract base currency and create the market symbol (e.g., "BTC/USDT" -> "BTCUSDT")
  const getMarketSymbol = useCallback(() => {
    if (!pair) return '';
    // Check if direct match exists
    if (marketData && marketData[pair]) return pair;
    // Check if stripped match exists
    const stripped = pair.replace('/', '');
    if (marketData && marketData[stripped]) return stripped;
    // Default to pair
    return pair; 
  }, [pair, marketData]);

  const marketSymbol = getMarketSymbol();

  const generateChartData = useCallback(() => {
    // console.log('Generating chart data for:', marketSymbol);
    
    // Fallback if no market data is provided
    let currentPrice = 1000;
    let priceChange = 0;

    const coinData = marketData ? (marketData[marketSymbol] || marketData[pair]) : null;

    if (coinData) {
      currentPrice = coinData.current_price || 1;
      priceChange = coinData.price_change_percentage_24h || 0;
    } else {
      // console.log('No market data found for symbol:', marketSymbol, 'Using mock data.');
      // Generate somewhat realistic price based on symbol hash if possible, or random
      currentPrice = 100 + Math.random() * 5000; 
    }

    console.log('Current price for', marketSymbol, ':', currentPrice);

    const dataPoints = 
      timeframe === '15m' ? 15 : 
      timeframe === '1h' ? 60 : 
      timeframe === '4h' ? 24 : 
      timeframe === '1d' ? 30 : 
      timeframe === '1w' ? 52 : 60;

    const data = [];
    let baseTime = new Date();
    
    // Set base time based on timeframe
    switch(timeframe) {
      case '15m':
        baseTime.setMinutes(baseTime.getMinutes() - 15);
        break;
      case '1h':
        baseTime.setHours(baseTime.getHours() - 1);
        break;
      case '4h':
        baseTime.setHours(baseTime.getHours() - 4);
        break;
      case '1d':
        baseTime.setDate(baseTime.getDate() - 1);
        break;
      case '1w':
        baseTime.setDate(baseTime.getDate() - 7);
        break;
      default:
        baseTime.setHours(baseTime.getHours() - 1);
    }

    // Use the actual price change for more realistic data
    const initialVolatility = Math.max(Math.abs(priceChange / 1000), 0.005); // Minimum 0.5% volatility

    // Generate historical data points
    let lastPrice = currentPrice;
    for (let i = 0; i < dataPoints; i++) {
      const time = new Date(baseTime);
      
      // Increment time based on timeframe
      switch(timeframe) {
        case '15m':
          time.setMinutes(time.getMinutes() + i);
          break;
        case '1h':
          time.setMinutes(time.getMinutes() + i);
          break;
        case '4h':
          time.setMinutes(time.getMinutes() + (i * 4));
          break;
        case '1d':
          time.setHours(time.getHours() + i);
          break;
        case '1w':
          time.setDate(time.getDate() + i);
          break;
        default:
          time.setMinutes(time.getMinutes() + i);
      }
      
      // Gradually reduce volatility as we go back in time
      const progress = i / dataPoints;
      const volatility = initialVolatility * (0.3 + 0.7 * progress); // Start with lower volatility, increase towards current
      
      // Generate price movement with some trend based on actual price change
      const trend = priceChange > 0 ? 0.1 : -0.1;
      const randomChange = (Math.random() - 0.5 + trend) * volatility;
      const price = lastPrice * (1 + randomChange);

      // Ensure price doesn't go negative
      const safePrice = Math.max(price, currentPrice * 0.01);
      
      const volume = Math.random() * 1000 + 500;

      // Add check to ensure safePrice is a valid number
      if (!isNaN(safePrice)) {
          data.unshift({
            time: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            timestamp: time.getTime(),
            price: parseFloat(safePrice.toFixed(6)),
            high: parseFloat((safePrice * (1 + Math.random() * 0.02)).toFixed(6)),
            low: parseFloat((safePrice * (1 - Math.random() * 0.02)).toFixed(6)),
            open: lastPrice,
            volume: parseFloat(volume.toFixed(2))
          });
          lastPrice = safePrice;
      }
    }

    // Add current price as the most recent point
    const currentTime = new Date();
    // Safely get last data point or default
    const lastDataPoint = data[0] || { price: currentPrice, high: currentPrice, low: currentPrice };
    
    // Ensure currentPrice is valid
    const safeCurrentPrice = !isNaN(currentPrice) ? currentPrice : 100;

    data.unshift({
      time: currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      timestamp: currentTime.getTime(),
      price: parseFloat(safeCurrentPrice.toFixed(6)),
      high: parseFloat(Math.max(lastDataPoint.high || safeCurrentPrice, safeCurrentPrice).toFixed(6)),
      low: parseFloat(Math.min(lastDataPoint.low || safeCurrentPrice, safeCurrentPrice).toFixed(6)),
      open: lastDataPoint.price || safeCurrentPrice,
      volume: parseFloat((Math.random() * 1000 + 500).toFixed(2))
    });

    console.log('Generated', data.length, 'data points for chart');
    setChartData(data);
    setIsLoading(false);
  }, [marketSymbol, timeframe, marketData]);

  // Initial generation
  useEffect(() => {
    // Only generate if we don't have data
    if (chartData.length === 0) {
      generateChartData();
    }
  }, [generateChartData, chartData.length]);

  // Update chart when marketData prop changes (for when socket is not available or using parent data)
  useEffect(() => {
    // If socket is available, let the socket listener handle it to avoid duplicate updates
    if (socket) return;
    
    if (!marketData || !marketSymbol || !marketData[marketSymbol]) return;
    
    const coinData = marketData[marketSymbol];
    const currentPrice = coinData.current_price;

    setChartData(prevData => {
      if (prevData.length === 0) {
        // If no data, let the generateChartData effect handle it
        return prevData; 
      }

      const newData = [...prevData];
      const lastPoint = newData[0];
      const currentTime = new Date();
      
      // Update the latest point to match current price
      newData[0] = {
        ...lastPoint,
        price: parseFloat(currentPrice.toFixed(6)),
        high: parseFloat(Math.max(lastPoint.high, currentPrice).toFixed(6)),
        low: parseFloat(Math.min(lastPoint.low, currentPrice).toFixed(6)),
        time: currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        volume: lastPoint.volume // Keep volume as is or increment slightly
      };
      
      return newData;
    });
  }, [marketData, marketSymbol, socket]);

  useEffect(() => {
    if (!socket) return;

    const handleMarketDataUpdate = (newMarketData) => {
      console.log('Market data update received');
      if (newMarketData && marketSymbol && newMarketData[marketSymbol]) {
        const currentPrice = newMarketData[marketSymbol].current_price;
        
        setChartData(prevData => {
          if (prevData.length === 0) {
            generateChartData();
            return prevData;
          }

          const newData = [...prevData];
          const currentTime = new Date();
          
          // Update the most recent data point with current price
          newData[0] = {
            ...newData[0],
            time: currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            price: parseFloat(currentPrice.toFixed(6)),
            high: parseFloat(Math.max(newData[0].high, currentPrice).toFixed(6)),
            low: parseFloat(Math.min(newData[0].low, currentPrice).toFixed(6)),
            volume: newData[0].volume + Math.random() * 100
          };

          return newData;
        });
      }
    };

    socket.on('marketData', handleMarketDataUpdate);
    
    return () => {
      socket.off('marketData', handleMarketDataUpdate);
    };
  }, [socket, marketSymbol, generateChartData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="chart-tooltip" style={{
          backgroundColor: '#1e2a3a',
          border: '1px solid #2a3b52',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
        }}>
          <p style={{ margin: '2px 0', fontWeight: 'bold', color: '#b0b8c4' }}>{label}</p>
          <p style={{ margin: '2px 0', color: '#b0b8c4' }}>
            Price: <span style={{ color: '#fff' }}>${data.price.toFixed(6)}</span>
          </p>
          <p style={{ margin: '2px 0', color: '#b0b8c4' }}>
            High: <span style={{ color: '#00c853' }}>${data.high.toFixed(6)}</span>
          </p>
          <p style={{ margin: '2px 0', color: '#b0b8c4' }}>
            Low: <span style={{ color: '#ff1744' }}>${data.low.toFixed(6)}</span>
          </p>
          <p style={{ margin: '2px 0', color: '#b0b8c4' }}>
            Volume: <span style={{ color: '#fff' }}>{data.volume.toFixed(2)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const getChartColor = () => {
    if (chartData.length < 2) return '#2962ff';
    const firstPrice = chartData[chartData.length - 1].price; // Oldest price
    const lastPrice = chartData[0].price; // Newest price
    return lastPrice >= firstPrice ? '#00c853' : '#ff1744';
  };

  // Get current price data for display
  const getCurrentPriceData = () => {
    if (marketData && marketSymbol && marketData[marketSymbol]) {
      return marketData[marketSymbol];
    }
    // Return dummy data if real data is missing to prevent crash
    return {
      current_price: chartData.length > 0 ? chartData[0].price : 0,
      price_change_percentage_24h: 0
    };
  };

  const currentPriceData = getCurrentPriceData();

  if (!pair) {
    return (
      <div className="trading-chart-container">
        <div className="no-pair-message" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          color: '#b0b8c4',
          fontSize: '16px'
        }}>
          Please select a trading pair to view the chart
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="trading-chart-container">
        <div className="chart-loading" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          color: '#b0b8c4',
          fontSize: '16px'
        }}>
          Loading chart data for {pair}...
        </div>
      </div>
    );
  }

  if (!currentPriceData) {
    return (
      <div className="trading-chart-container">
        <div className="no-data-message" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          color: '#b0b8c4',
          fontSize: '16px',
          flexDirection: 'column',
          gap: '10px'
        }}>
          <div>No market data available for {pair}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Symbol: {marketSymbol}
          </div>
          <button 
            onClick={generateChartData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2962ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="trading-chart-container">
        <div className="no-chart-data" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          color: '#b0b8c4',
          fontSize: '16px'
        }}>
          Could not generate chart data
        </div>
      </div>
    );
  }

  return (
    <div className="trading-chart-container">
      <div className="chart-controls" style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px',
        borderBottom: '1px solid #2a3b52',
        backgroundColor: '#1a2332',
        gap: '10px'
      }}>
        <div className="timeframe-selector" style={{ display: 'flex', gap: '5px' }}>
          {['15m', '1h', '4h', '1d', '1w'].map((tf) => (
            <button
              key={tf}
              className={`timeframe-btn ${timeframe === tf ? 'active' : ''}`}
              onClick={() => setTimeframe(tf)}
              style={{
                padding: '4px 8px',
                border: '1px solid #2a3b52',
                backgroundColor: timeframe === tf ? '#2962ff' : 'transparent',
                color: timeframe === tf ? 'white' : '#b0b8c4',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px'
              }}
            >
              {tf}
            </button>
          ))}
        </div>
        
        <div className="chart-type-selector" style={{ display: 'flex', gap: '5px' }}>
          <button
            className={`chart-type-btn ${chartType === 'line' ? 'active' : ''}`}
            onClick={() => setChartType('line')}
            style={{
              padding: '4px 8px',
              border: '1px solid #2a3b52',
              backgroundColor: chartType === 'line' ? '#2962ff' : 'transparent',
              color: chartType === 'line' ? 'white' : '#b0b8c4',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            Line
          </button>
          <button
            className={`chart-type-btn ${chartType === 'area' ? 'active' : ''}`}
            onClick={() => setChartType('area')}
            style={{
              padding: '4px 8px',
              border: '1px solid #2a3b52',
              backgroundColor: chartType === 'area' ? '#2962ff' : 'transparent',
              color: chartType === 'area' ? 'white' : '#b0b8c4',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            Area
          </button>
        </div>

        <div className="chart-info" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span className="pair-name" style={{ color: '#b0b8c4', fontWeight: 'bold', fontSize: '12px' }}>
            {pair}
          </span>
          <span className="current-price" style={{ 
            color: getChartColor(),
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            ${currentPriceData.current_price?.toFixed(6) || '0.000000'}
          </span>
          <span className={`price-change ${
            currentPriceData.price_change_percentage_24h >= 0 ? 'positive' : 'negative'
          }`} style={{
            color: currentPriceData.price_change_percentage_24h >= 0 ? '#00c853' : '#ff1744',
            fontSize: '12px'
          }}>
            {currentPriceData.price_change_percentage_24h >= 0 ? '+' : ''}
            {currentPriceData.price_change_percentage_24h?.toFixed(2) || '0.00'}%
          </span>
        </div>
      </div>

      <div className="chart-wrapper" ref={chartRef} style={{ width: '100%', height: '300px', minHeight: '300px' }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={300}>
          {chartType === 'line' ? (
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a3b52" />
              <XAxis 
                dataKey="time" 
                stroke="#b0b8c4"
                fontSize={12}
                tickCount={6}
                interval="preserveStartEnd"
              />
              <YAxis 
                stroke="#b0b8c4"
                fontSize={12}
                domain={['dataMin', 'dataMax']}
                tickFormatter={(value) => `$${value.toFixed(4)}`}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="price" 
                stroke={getChartColor()}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6, fill: getChartColor(), stroke: '#fff', strokeWidth: 2 }}
                isAnimationActive={false}
              />
            </LineChart>
          ) : (
            <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a3b52" />
              <XAxis 
                dataKey="time" 
                stroke="#b0b8c4"
                fontSize={12}
                tickCount={6}
                interval="preserveStartEnd"
              />
              <YAxis 
                stroke="#b0b8c4"
                fontSize={12}
                domain={['dataMin', 'dataMax']}
                tickFormatter={(value) => `$${value.toFixed(4)}`}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} />
              <defs>
                <linearGradient id={`colorPrice-${marketSymbol.replace(/[^a-zA-Z0-9]/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={getChartColor()} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={getChartColor()} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <Area 
                type="monotone" 
                dataKey="price" 
                stroke={getChartColor()}
                fill={`url(#colorPrice-${marketSymbol.replace(/[^a-zA-Z0-9]/g, '')})`}
                strokeWidth={3}
                isAnimationActive={false}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="volume-chart" style={{ width: '100%', height: '60px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 0, right: 30, left: 20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a3b52" />
            <XAxis 
              dataKey="time" 
              stroke="#b0b8c4"
              fontSize={10}
              tickCount={4}
              interval="preserveStartEnd"
              hide={true}
            />
            <YAxis 
              stroke="#b0b8c4"
              fontSize={10}
              width={40}
              tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
              domain={['auto', 'auto']}
            />
            <Tooltip />
            <defs>
              <linearGradient id={`colorVolume-${marketSymbol}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2962ff" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#2962ff" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <Area 
              type="monotone" 
              dataKey="volume" 
              stroke="#2962ff"
              fill={`url(#colorVolume-${marketSymbol})`}
              strokeWidth={1}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default TradingChart;